import { useState } from 'react';
import { Link } from 'react-router-dom';

/**
 * Result Banner Component
 * 
 * Displays success/error results with requestId, auditEventId, and action links.
 */
export default function ResultBanner({ type, message, requestId, auditEventId, errorCode, onClose }) {
  const [copiedField, setCopiedField] = useState(null);

  const copyToClipboard = async (text, fieldName) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopiedField(fieldName);
      setTimeout(() => setCopiedField(null), 2000);
    } catch (err) {
      console.error('Failed to copy to clipboard:', err);
    }
  };

  const isSuccess = type === 'success';
  const isInfo = type === 'info';
  const bgColor = isSuccess
    ? 'bg-green-50 border-green-200'
    : isInfo
    ? 'bg-blue-50 border-blue-200'
    : 'bg-red-50 border-red-200';
  const textColor = isSuccess
    ? 'text-green-800'
    : isInfo
    ? 'text-blue-800'
    : 'text-red-800';
  const borderColor = isSuccess
    ? 'border-green-200'
    : isInfo
    ? 'border-blue-200'
    : 'border-red-200';

  return (
    <div className={`${bgColor} border ${borderColor} rounded-lg p-4 mb-4`}>
      <div className="flex justify-between items-start">
        <div className="flex-1">
          <div className={`font-semibold ${textColor} mb-2`}>
            {isSuccess ? '✓ Success' : isInfo ? 'ℹ Info' : '✗ Error'}
          </div>
          <div className={`text-sm ${textColor} mb-3`}>{message}</div>
          {errorCode && (
            <div className={`text-xs ${textColor} mb-2 font-mono`}>
              Error Code: {errorCode}
              {errorCode === 'ACCESS_DENIED' && (
                <span className="ml-2 text-xs font-normal">(Access Denied)</span>
              )}
              {(errorCode === 'MUTATION_FROZEN' || errorCode === 'POLICY_BLOCKED' || errorCode === 'EGRESS_BLOCKED') && (
                <span className="ml-2 text-xs font-normal">(Action Blocked)</span>
              )}
            </div>
          )}

          {/* Request ID and Audit Event ID */}
          <div className="space-y-2 text-xs">
            {requestId && (
              <div className="flex items-center gap-2">
                <span className={`font-medium ${textColor}`}>Request ID:</span>
                <code className="flex-1 bg-white px-2 py-1 rounded font-mono text-xs">
                  {requestId}
                </code>
                <button
                  onClick={() => copyToClipboard(requestId, 'requestId')}
                  className="px-2 py-1 bg-white border border-gray-300 rounded text-xs hover:bg-gray-50"
                >
                  {copiedField === 'requestId' ? '✓ Copied' : 'Copy'}
                </button>
                <Link
                  to={`/admin/activity?request_id=${encodeURIComponent(requestId)}`}
                  className="px-2 py-1 bg-white border border-gray-300 rounded text-xs hover:bg-gray-50 text-blue-600"
                >
                  View in Audit
                </Link>
              </div>
            )}

            {auditEventId && (
              <div className="flex items-center gap-2">
                <span className={`font-medium ${textColor}`}>Audit Event ID:</span>
                <code className="flex-1 bg-white px-2 py-1 rounded font-mono text-xs">
                  {auditEventId}
                </code>
                <button
                  onClick={() => copyToClipboard(auditEventId.toString(), 'auditEventId')}
                  className="px-2 py-1 bg-white border border-gray-300 rounded text-xs hover:bg-gray-50"
                >
                  {copiedField === 'auditEventId' ? '✓ Copied' : 'Copy'}
                </button>
              </div>
            )}
          </div>
        </div>

        {onClose && (
          <button
            onClick={onClose}
            className={`ml-4 text-${isSuccess ? 'green' : 'red'}-600 hover:text-${isSuccess ? 'green' : 'red'}-800`}
          >
            ×
          </button>
        )}
      </div>
    </div>
  );
}

